/**
 * ANDOSource — provenance for a citation in an agent answer.
 *
 * Six source categories (Nov 2025 + Jan 2026 + Feb 2026 SOTA):
 *   - document: indexed RAG result (drive / minio / manual_upload)
 *   - web: tavily / web_search specialist
 *   - email: gmail / outlook EmailChunks
 *   - chat: teams / google_chat / slack messages
 *
 * v1 ref: `ando-frontend/app/types/chat.ts:22-68`.
 */

import { z } from "zod";

export const AndoSourceTypeSchema = z.enum(["web", "document", "email", "chat"]);
export type AndoSourceType = z.infer<typeof AndoSourceTypeSchema>;

export const AndoSourceSchema = z.object({
  // Core identity
  doc_id: z.string(),
  source_file: z.string().optional(),
  page: z.number().default(0),
  relevance_score: z.number().default(0),

  // Reranker / hybrid scoring — different specialists ship different
  // fields, so the FE picks the highest-information one in
  // ``computeRelevance``. ``rerank_score`` is Jina v3 logits (0..1,
  // higher = better) when reranking ran; ``final_score`` is the unified
  // hybrid metric (0..1, LOWER = better — orchestration_patterns
  // policy). Web sources set ``relevance_score`` directly; docs/email
  // leave it 0 and rely on the score fields above.
  rerank_score: z.number().optional(),
  final_score: z.number().optional(),

  // Imagery
  has_images: z.boolean().default(false),
  images: z.array(z.string()).default([]),

  // Source category
  source_type: AndoSourceTypeSchema.optional(),

  // Drive / MinIO
  drive_source: z.string().optional(),
  drive_web_link: z.string().optional(),
  drive_file_id: z.string().optional(),
  minio_key: z.string().optional(),
  proxy_url: z.string().optional(),
  user_id: z.string().optional(),

  // Generic doc metadata
  content_type: z.string().optional(),
  folder_path: z.string().optional(),
  author: z.string().optional(),
  modified_date: z.string().optional(),
  chunk_type: z.string().optional(),

  // Web
  url: z.string().optional(),
  published_date: z.string().optional(),
  content: z.string().optional(),

  // Email
  message_id: z.string().optional(),
  subject: z.string().optional(),
  sender: z.string().optional(),
  sender_name: z.string().optional(),
  recipients: z.array(z.string()).optional(),
  sent_at: z.string().optional(),
  web_link: z.string().optional(),
  has_attachments: z.boolean().optional(),
  attachment_names: z.array(z.string()).optional(),
  email_snippet: z.string().optional(),

  // Chat (Teams / Slack / Google Chat)
  provider: z.string().optional(),
  conversation_id: z.string().optional(),
  participant_names: z.array(z.string()).optional(),
  team_name: z.string().optional(),
  channel_name: z.string().optional(),
  space_name: z.string().optional(),
  last_message_at: z.string().optional(),
  text: z.string().optional(),
  source_link: z.string().optional(),
});

export type AndoSource = z.infer<typeof AndoSourceSchema>;

/**
 * Raw context excerpt — the actual text chunk that fed the agent. Shown
 * inline below the answer when the user clicks "show sources".
 */
export const AndoRawContextSchema = z.object({
  doc_id: z.string(),
  page: z.number().default(0),
  text: z.string(),
  images: z.array(z.string()).default([]),
  relevance_score: z.number().default(0),
});

export type AndoRawContext = z.infer<typeof AndoRawContextSchema>;
